"use client";

import Image from "next/image";
import { Suspense, createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { motion, useReducedMotion } from "motion/react";
import { Link, useRouter } from "@/i18n/navigation";
import { labels } from "@/i18n/routing";
import { articleCoverPath } from "@/helpers/articleCover";
import {
    ARTICLE_TOPICS,
    articleSubtopicLabel,
    articleTopicPath,
    getArticleTopic,
    isArticleTopic,
    topicFromLegacyCategory,
} from "@/lib/articles/topics";
import Alert from "@/components/ui/Alert/Alert";
import BookmarkButton from "@/components/ui/BookmarkButton/BookmarkButton";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// La liste des sujets, avec les sous-thèmes créés depuis l'administration quand
// la page les a lus (lib/articles/topics.js, mergeArticleTopics). Sans eux, un
// sous-thème créé n'aurait ni onglet ni adresse.
const TopicsContext = createContext(ARTICLE_TOPICS);

// Une rangée de filtres qui défile sur téléphone : le bord droit s'estompe pour
// montrer qu'il reste des choix hors de l'écran, et la marge de fin laisse le
// dernier sortir de l'estompe.
const SCROLL_ROW =
    "-mx-5 px-5 md:mx-0 md:px-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black_calc(100%-3rem),transparent)] md:[mask-image:none]";

// Le jour de publication, au fuseau de Paris pour tout le monde. Le calculer
// au fuseau du visiteur donnait un jour côté serveur et parfois un autre dans
// le navigateur pour un article publié vers minuit.
function useFormatDate() {
    const locale = useLocale();

    return (iso) =>
        new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "Europe/Paris",
        }).format(new Date(iso));
}

/** Sa vignette, ou celle qu'on dessine à sa place (voir helpers/articleCover.js). */
function Cover({ article, sizes, priority = false }) {
    const locale = useLocale();
    const src = articleCoverPath(article, locale);
    if (!src) return null;

    return (
        <Image
            src={src}
            alt=""
            fill
            sizes={sizes}
            priority={priority}
            unoptimized={!article.cover}
            className="object-cover"
        />
    );
}

function Meta({ article, withTopic }) {
    const t = useTranslations();
    const formatDate = useFormatDate();

    return (
        <span className="flex flex-wrap gap-x-2 text-xs">
            {withTopic && isArticleTopic(article.topic) && (
                <span className="font-medium text-gray-700 dark:text-grayDark-300">
                    {t(`articles_topic_${article.topic}`)}
                </span>
            )}
            <time dateTime={article.dateCreation} className="text-gray-500 dark:text-grayDark-400">
                {formatDate(article.dateCreation)}
            </time>
        </span>
    );
}

/** La carte de la grille. Le signet ne s'affiche qu'à un membre connecté. */
function ArticleCard({ article, bookmark }) {
    return (
        <div className="h-full relative">
            {bookmark && (
                <div className="absolute top-2 right-2 z-20">
                    <BookmarkButton
                        articleId={article.id}
                        initialBookmarked={bookmark.ids.has(article.id)}
                        size="small"
                    />
                </div>
            )}
            <Link href={`/r/${article.slug}`} className="h-full">
                <div className="border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840 rounded-xl overflow-hidden group hover:bg-gray-50 dark:hover:bg-bgDark hover:border-primary dark:hover:border-primary transition-all duration-200 h-full flex flex-col">
                    <div className="aspect-[16/9] relative bg-primary/10 dark:bg-primary/20">
                        <Cover
                            article={article}
                            sizes="(min-width: 1280px) 400px, (min-width: 768px) 50vw, 100vw"
                        />
                    </div>
                    <div className="flex-1 flex flex-col p-5">
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-primary font-sourceSans line-clamp-2">
                                {article.title}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-grayDark-400 font-geistSans mt-2 line-clamp-2 mb-0">
                                {article.description}
                            </p>
                        </div>
                        <div className="mt-4">
                            <Meta article={article} />
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
}

/** Une ligne : titre, date, petite vignette. Pour les plus lus, et pour les sujets sur téléphone. */
function ArticleRow({ article, rank, withTopic }) {
    return (
        <Link href={`/r/${article.slug}`} className="group flex items-center gap-4 py-4">
            {rank && (
                <span className="w-5 shrink-0 text-xl font-bold font-sourceSans text-gray-300 dark:text-grayDark-600 tabular-nums">
                    {rank}
                </span>
            )}
            <span className="flex-1 min-w-0">
                <span className="block font-semibold font-sourceSans text-gray-900 dark:text-white group-hover:text-primary leading-snug line-clamp-2">
                    {article.title}
                </span>
                <span className="block mt-1">
                    <Meta article={article} withTopic={withTopic} />
                </span>
            </span>
            <span className="w-24 shrink-0 aspect-[16/10] relative rounded-lg overflow-hidden bg-primary/10 dark:bg-primary/20">
                <Cover article={article} sizes="96px" />
            </span>
        </Link>
    );
}

// ---------------------------------------------------------------------------
// Le passage d'un onglet à l'autre
//
// Chaque onglet est une page. Sans rien faire, cliquer remplace d'un coup tout
// ce qui est sous les filtres. On joue donc le changement en trois temps, dans
// le sens du déplacement de la pastille :
//
//   1. au clic, la page en place s'estompe et recule de quelques pixels ;
//   2. la page suivante arrive du côté vers lequel la pastille est partie ;
//   3. ses cartes se posent l'une après l'autre.
//
// Rien de tout cela ne joue au premier affichage : le HTML servi est complet et
// visible, l'animation ne concerne que le passage d'un onglet à un autre.
// ---------------------------------------------------------------------------

const EASE = [0.22, 1, 0.36, 1];

// Le dernier clic sur un onglet, un sous-thème ou un numéro de page. Hors des
// composants, parce que la page qui arrive est un nouvel arbre : c'est le seul
// endroit où elle peut apprendre d'où l'on vient.
let lastSwitch = null;

// Au-delà, on n'arrive plus d'un clic sur un onglet mais d'ailleurs (retour
// arrière, lien dans un article) : la page s'affiche sans animation.
const SWITCH_MEMORY_MS = 4000;

/** Le clic qui a amené sur cette page, s'il vient d'avoir lieu. */
function recentSwitch() {
    return lastSwitch && Date.now() - lastSwitch.at < SWITCH_MEMORY_MS ? lastSwitch : null;
}

// Ce que les blocs d'une page savent du passage en cours.
//
// - `arrival` : le clic qui a amené ici, { direction, scope }. `direction` vaut
//   1 quand on vient d'un onglet situé à gauche, -1 à droite, 0 pour un
//   changement de page. `scope` dit ce qui a changé : le sujet ("topic"), le
//   sous-thème ("sub") ou la page ("page"). null : rien à jouer (premier
//   affichage, animations réduites).
// - `leaving` : le clic qui est en train de faire quitter cette page, même forme.
// - `onSwitch(direction, scope)` : à appeler au clic sur un onglet.
const SwitchContext = createContext({
    arrival: null,
    leaving: null,
    onSwitch: () => {},
});

/** Vrai quand ce passage change au moins ce niveau : un sujet change aussi le sous-thème et la page. */
const reaches = (move, scope) =>
    Boolean(move) && ["topic", "sub", "page"].indexOf(move.scope) <= ["topic", "sub", "page"].indexOf(scope);

/**
 * Un bloc qui se pose à l'arrivée d'une page : il monte de quelques pixels en
 * apparaissant, avec un léger retard selon son rang. Sans arrivée à jouer, un
 * simple <div>.
 */
function Reveal({ index = 0, className, children }) {
    const { arrival } = useContext(SwitchContext);
    if (!arrival) return <div className={className}>{children}</div>;

    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.42,
                ease: EASE,
                // Le retard plafonne : la douzième carte n'attend pas une demi-seconde.
                delay: 0.06 + Math.min(index, 8) * 0.035,
            }}
        >
            {children}
        </motion.div>
    );
}

/**
 * Une zone qui change avec les onglets. Elle arrive du côté où la pastille est
 * partie, et s'estompe en reculant dès qu'un autre onglet est cliqué.
 *
 * @param {"topic"|"sub"|"page"} props.scope Le niveau à partir duquel la zone
 *        change : le titre ne bouge pas quand on change de page, la grille si.
 * @param {boolean} [props.slide] Faux pour un simple fondu, sans déplacement
 *        (le titre, au-dessus des onglets, ne suit pas la pastille).
 */
function SwitchingArea({ scope, slide = true, className, children }) {
    const move = useContext(SwitchContext);
    const arrival = reaches(move.arrival, scope) ? move.arrival : null;
    const leaving = reaches(move.leaving, scope) ? move.leaving : null;
    const shift = slide ? 1 : 0;

    return (
        <motion.div
            className={className}
            initial={arrival ? { opacity: 0, x: arrival.direction * 28 * shift } : false}
            animate={leaving ? { opacity: 0.25, x: leaving.direction * -12 * shift } : { opacity: 1, x: 0 }}
            transition={leaving ? { duration: 0.16, ease: "easeOut" } : { duration: 0.45, ease: EASE }}
        >
            {children}
        </motion.div>
    );
}

/**
 * Porte le passage en cours pour une vue (la vue d'ensemble, un sujet, un
 * sous-thème, une page). Montée avec une clé par vue : elle lit donc à chaque
 * nouvelle vue le clic qui y a mené.
 */
function SwitchProvider({ view, children }) {
    const reduceMotion = useReducedMotion();

    // Lu une seule fois, à la création de la vue.
    const [arrival] = useState(() => recentSwitch());
    const [leaving, setLeaving] = useState(null);

    useEffect(() => {
        // Une vue retrouvée par le bouton Précédent revient telle qu'on l'a
        // quittée, donc estompée : on la rallume.
        setLeaving(null);
    }, []);

    useEffect(() => {
        if (!leaving) return;
        // La page suivante n'est jamais arrivée (réseau coupé) : on ne laisse
        // pas celle-ci à moitié effacée.
        const timer = setTimeout(() => setLeaving(null), 4000);
        return () => clearTimeout(timer);
    }, [leaving]);

    const onSwitch = (direction, scope) => {
        // Un clic sur l'onglet déjà ouvert ne mène nulle part.
        if (direction === 0 && scope !== "page") return;

        lastSwitch = { at: Date.now(), direction, scope, from: view };
        setLeaving({ direction, scope });
    };

    return (
        <SwitchContext.Provider
            value={{
                // On n'anime pas une arrivée sur la vue qu'on vient de quitter.
                arrival: reduceMotion || arrival?.from === view ? null : arrival,
                leaving: reduceMotion ? null : leaving,
                onSwitch,
            }}
        >
            {children}
        </SwitchContext.Provider>
    );
}

// Où se trouvait l'indicateur de chaque rangée d'onglets au dernier clic. Hors
// des composants, parce que changer de sujet change de page : la rangée est
// détruite puis recréée, et sans ce souvenir l'indicateur réapparaîtrait
// directement sous le nouvel onglet au lieu d'y glisser.
const lastIndicators = new Map();

// Passé ce délai, le souvenir ne vient plus du clic qui a amené ici : on
// l'ignore, pour ne pas faire glisser l'indicateur depuis un onglet quitté il
// y a dix minutes. Large, parce qu'une page pas encore en cache peut mettre
// plus d'une seconde à arriver.
const INDICATOR_MEMORY_MS = 4000;

const INDICATOR_MS = 380;
const INDICATOR_EASING = [0.22, 1, 0.36, 1];
const INDICATOR_TRANSITION = ["transform", "width", "height", "clip-path"]
    .map((property) => `${property} ${INDICATOR_MS}ms cubic-bezier(${INDICATOR_EASING.join(", ")})`)
    .join(", ");

/** L'avancement d'une courbe de Bézier cubique au temps t (0 à 1), comme le calcule le navigateur. */
function easeAt(t, [x1, y1, x2, y2]) {
    const curve = (a, b, u) => 3 * a * u * (1 - u) ** 2 + 3 * b * u ** 2 * (1 - u) + u ** 3;

    // On cherche le paramètre u dont l'abscisse vaut t, par dichotomie.
    let low = 0;
    let high = 1;
    for (let step = 0; step < 20; step++) {
        const middle = (low + high) / 2;
        if (curve(x1, x2, middle) < t) low = middle;
        else high = middle;
    }

    return curve(y1, y2, (low + high) / 2);
}

/** Où en est un glissement mémorisé, à l'instant présent. */
function indicatorNow({ from, to, at }) {
    const progress = easeAt(Math.min(1, (Date.now() - at) / INDICATOR_MS), INDICATOR_EASING);
    const between = (key) => from[key] + (to[key] - from[key]) * progress;

    return {
        x: between("x"),
        y: between("y"),
        width: between("width"),
        height: between("height"),
    };
}

/**
 * Un indicateur qui glisse d'un onglet à l'autre.
 *
 * Il part dès le clic, sans attendre la page suivante (`pick`), puis la rangée
 * recréée par la nouvelle page le reprend là où il était et finit le trajet.
 * Le lecteur voit un seul mouvement continu.
 *
 * @param {string} row    Nom de la rangée, pour retrouver son souvenir.
 * @param {string} active Clé de l'onglet de la page affichée.
 * @returns {{containerRef, setItem, shown: string, pick: Function, box: object|null, transition: string}}
 *          `shown` est l'onglet à dessiner comme actif ; `box` (position et
 *          taille de l'indicateur) vaut null tant que rien n'est mesuré (rendu
 *          serveur, premier affichage) : l'onglet actif porte alors lui-même
 *          sa couleur.
 */
function useSlidingIndicator(row, active) {
    const containerRef = useRef(null);
    const items = useRef(new Map());
    const firstMeasure = useRef(true);

    // L'onglet cliqué, tant que sa page n'est pas arrivée.
    const [picked, setPicked] = useState(null);
    const shown = picked?.from === active ? picked.key : active;

    const [box, setBox] = useState(null);
    const [moving, setMoving] = useState(false);

    const measure = (key) => {
        const item = items.current.get(key);
        if (!item) return null;
        return {
            x: item.offsetLeft,
            y: item.offsetTop,
            width: item.offsetWidth,
            height: item.offsetHeight,
        };
    };

    // La dernière position demandée, pour savoir d'où part le glissement suivant.
    const placed = useRef(null);

    useLayoutEffect(() => {
        const place = (animated, from = placed.current) => {
            const next = measure(shown);
            if (!next) return;

            // Sur téléphone la rangée défile : on amène l'onglet actif au
            // milieu, sinon on arrive sur un sujet dont l'onglet est hors de
            // l'écran. Sans effet quand la rangée tient en largeur.
            const scroller = containerRef.current?.parentElement;
            const scrolledFrom = scroller?.scrollLeft || 0;
            scroller?.scrollTo({
                left: next.x - (scroller.clientWidth - next.width) / 2,
                behavior: animated ? "smooth" : "auto",
            });

            // On retient le trajet et son heure de départ, pas seulement
            // l'arrivée : si la page change en plein glissement, la rangée
            // suivante reprend l'indicateur à l'endroit exact où il en était.
            lastIndicators.set(row, {
                from: animated && from ? from : next,
                to: next,
                at: Date.now(),
                scrolledFrom,
            });
            placed.current = next;
            setMoving(animated);
            setBox(next);
        };

        let frame = null;
        const remembered = lastIndicators.get(row);

        if (firstMeasure.current && remembered && Date.now() - remembered.at < INDICATOR_MEMORY_MS) {
            // La rangée vient d'être recréée par un changement de page : on pose
            // l'indicateur là où le glissement en était, puis on le laisse
            // finir son trajet une fois cette position peinte.
            const resumed = indicatorNow(remembered);
            // La rangée recréée repart du même défilement que celle qu'on quitte.
            const scroller = containerRef.current?.parentElement;
            if (scroller) scroller.scrollLeft = remembered.scrolledFrom || 0;
            setMoving(false);
            setBox(resumed);
            frame = requestAnimationFrame(() => {
                frame = requestAnimationFrame(() => {
                    frame = null;
                    place(true, indicatorNow(remembered));
                });
            });
        } else {
            place(!firstMeasure.current);
        }
        firstMeasure.current = false;

        return () => {
            if (!frame) return;
            cancelAnimationFrame(frame);
            // La reprise n'a pas eu lieu (en développement, React monte chaque
            // effet deux fois) : le prochain passage doit la refaire, sinon
            // l'indicateur irait droit à l'arrivée.
            firstMeasure.current = true;
        };
    }, [row, shown]);

    // Un changement de largeur (rotation de l'écran, police qui finit de
    // charger) replace l'indicateur, sans glissement. L'observateur prévient
    // aussi une première fois dès qu'on le branche : ce signal-là est ignoré,
    // il couperait le glissement en cours.
    const shownRef = useRef(shown);
    useEffect(() => {
        shownRef.current = shown;
    }, [shown]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let width = container.offsetWidth;
        const observer = new ResizeObserver(() => {
            if (container.offsetWidth === width) return;
            width = container.offsetWidth;

            const next = measure(shownRef.current);
            if (!next) return;
            placed.current = next;
            setMoving(false);
            setBox(next);
        });
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    return {
        containerRef,
        setItem: (key) => (node) => {
            if (node) items.current.set(key, node);
            else items.current.delete(key);
        },
        shown,
        pick: (key) => setPicked({ key, from: active }),
        box,
        transition: moving ? INDICATOR_TRANSITION : "none",
    };
}

/**
 * Les filtres par sujet. Ce sont des liens : chaque sujet est une page, qu'un
 * moteur de recherche suit comme un lecteur.
 *
 * La pastille orange glisse d'un sujet à l'autre (voir useSlidingIndicator), et
 * le texte change de couleur là où elle passe, lettre par lettre : les
 * libellés existent deux fois, en gris dessous et en blanc dessus, et la couche
 * blanche est découpée à la forme de la pastille. Faire basculer la couleur au
 * clic donnait un libellé blanc sur fond gris le temps que la pastille arrive.
 *
 * La couleur du texte vit sur un <span> intérieur et jamais sur le lien :
 * globals.css repeint tout lien survolé en rose (a:hover), ce qui l'emporte sur
 * une classe de couleur posée sur le <a> lui-même.
 */
function TopicTabs({ active, counts }) {
    const t = useTranslations();
    const { onSwitch } = useContext(SwitchContext);
    const locale = useLocale();
    const tabs = [{ id: "all" }, ...ARTICLE_TOPICS.filter((topic) => counts[topic.id] > 0)];
    const { containerRef, setItem, shown, pick, box, transition } = useSlidingIndicator("topics", active);

    const rank = (id) => tabs.findIndex((tab) => tab.id === id);
    const label = (tab) => t(tab.id === "all" ? "articles_category_all" : `articles_topic_${tab.id}`);

    // Les deux couches partagent ces classes : au pixel près, sinon le texte
    // blanc ne tomberait pas sur le texte gris.
    const row = "flex gap-2 w-max pr-8 md:w-auto md:pr-0 md:flex-wrap";
    const cell = "block px-4 py-2.5 rounded-xl";
    const text = "flex items-center gap-2 font-medium text-sm whitespace-nowrap";

    return (
        <nav className={SCROLL_ROW} aria-label={t("articles_topics_label")}>
            <div ref={containerRef} className={`relative ${row}`}>
                {tabs.map((tab) => {
                    // Avant la première mesure (HTML servi), l'onglet actif
                    // porte lui-même la couleur de la pastille.
                    const solid = !box && shown === tab.id;
                    return (
                        <Link
                            key={tab.id}
                            ref={setItem(tab.id)}
                            href={tab.id === "all" ? "/articles" : articleTopicPath(locale, tab.id)}
                            aria-current={active === tab.id ? "page" : undefined}
                            scroll={false}
                            onClick={() => {
                                pick(tab.id);
                                onSwitch(Math.sign(rank(tab.id) - rank(active)), "topic");
                            }}
                            // Pas de fondu sur le fond : l'onglet actif passe de l'orange au
                            // gris au moment où la pastille prend le relais, et un fondu
                            // laisserait un fantôme orange à l'arrivée.
                            className={`${cell} ${
                                solid
                                    ? "bg-primary"
                                    : "bg-gray-100 dark:bg-grayDark-800 hover:bg-gray-200 dark:hover:bg-grayDark-700"
                            }`}
                        >
                            <span
                                className={`${text} ${solid ? "text-white" : "text-gray-700 dark:text-grayDark-300"}`}
                            >
                                {tab.icon && <SolarIcon name={tab.icon} className="w-4 h-4" />}
                                {label(tab)}
                                {tab.id !== "all" && (
                                    <span
                                        className={`text-xs font-normal tabular-nums ${
                                            solid ? "text-white/80" : "text-gray-500 dark:text-grayDark-400"
                                        }`}
                                    >
                                        {counts[tab.id]}
                                    </span>
                                )}
                            </span>
                        </Link>
                    );
                })}

                {box && (
                    // La pastille : la rangée entière repeinte en orange et blanc,
                    // découpée à la forme de l'onglet actif. Une seule pièce, donc
                    // rien ne se décale en route. L'ombre est posée sur
                    // l'enveloppe : sur la couche découpée, elle serait coupée
                    // avec le reste.
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none [filter:drop-shadow(0_4px_6px_rgb(232_81_29_/_0.28))]"
                    >
                        <div
                            style={{
                                clipPath: `inset(${box.y}px calc(100% - ${box.x + box.width}px) calc(100% - ${
                                    box.y + box.height
                                }px) ${box.x}px round 0.75rem)`,
                                transition,
                            }}
                            className={`absolute inset-0 bg-primary ${row} motion-reduce:!transition-none`}
                        >
                            {tabs.map((tab) => (
                                <span key={tab.id} className={cell}>
                                    <span className={`${text} text-white`}>
                                        {tab.icon && <SolarIcon name={tab.icon} className="w-4 h-4" />}
                                        {label(tab)}
                                        {tab.id !== "all" && (
                                            <span className="text-xs font-normal tabular-nums text-white/80">
                                                {counts[tab.id]}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
}

/** La vue d'ensemble : le dernier article, les plus lus, puis trois articles par sujet. */
function Overview({ overview, bookmark }) {
    const t = useTranslations();
    const locale = useLocale();
    const { lead, mostRead, topics } = overview;

    return (
        <>
            <section className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
                {lead && (
                    <Reveal className={mostRead.length >= 3 ? "lg:col-span-3" : "lg:col-span-5"}>
                        <Link href={`/r/${lead.slug}`} className="group block">
                            <div className="aspect-[16/9] relative rounded-xl overflow-hidden border border-gray-200 dark:border-borderDark bg-primary/10 dark:bg-primary/20">
                                <Cover article={lead} sizes="(min-width: 1024px) 720px, 100vw" priority />
                            </div>
                            <div className="mt-5">
                                <Meta article={lead} withTopic />
                                <h2 className="mt-2 text-2xl md:text-3xl font-bold font-sourceSans text-gray-900 dark:text-white group-hover:text-primary leading-tight">
                                    {lead.title}
                                </h2>
                                <p className="mt-3 text-gray-600 dark:text-grayDark-400 mb-0 max-w-2xl">
                                    {lead.description}
                                </p>
                            </div>
                        </Link>
                    </Reveal>
                )}

                {/* En dessous de trois articles, le classement ferait pauvre : on ne l'affiche pas. */}
                {mostRead.length >= 3 && (
                    <Reveal index={1} className="lg:col-span-2">
                        <h2 className="text-lg font-bold font-sourceSans text-gray-900 dark:text-white">
                            {t("articles_most_read")}
                        </h2>
                        <ol className="mt-2 divide-y divide-gray-200 dark:divide-borderDark">
                            {mostRead.map((article, index) => (
                                <li key={article.id}>
                                    <ArticleRow article={article} rank={index + 1} withTopic />
                                </li>
                            ))}
                        </ol>
                    </Reveal>
                )}
            </section>

            {topics
                .filter((topic) => topic.articles.length > 0)
                .map((topic, index) => (
                    <Reveal key={topic.id} index={index + 2} className="mt-14">
                        <section>
                            <div className="flex items-baseline justify-between gap-4 mb-5">
                                <h2 className="text-xl md:text-2xl font-bold font-sourceSans text-gray-900 dark:text-white">
                                    {t(`articles_topic_${topic.id}`)}
                                </h2>
                                <Link
                                    href={articleTopicPath(locale, topic.id)}
                                    className="group hidden md:block"
                                >
                                    <span className="text-sm font-medium text-gray-700 dark:text-grayDark-300 underline decoration-gray-300 dark:decoration-grayDark-600 underline-offset-4 group-hover:text-primary group-hover:decoration-primary whitespace-nowrap">
                                        {t("articles_see_topic", { count: topic.count })}
                                    </span>
                                </Link>
                            </div>

                            <div className="hidden md:grid md:grid-cols-3 gap-5">
                                {topic.articles.map((article) => (
                                    <ArticleCard key={article.id} article={article} bookmark={bookmark} />
                                ))}
                            </div>
                            <div className="md:hidden divide-y divide-gray-200 dark:divide-borderDark border-y border-gray-200 dark:border-borderDark">
                                {topic.articles.map((article) => (
                                    <ArticleRow key={article.id} article={article} />
                                ))}
                            </div>
                            {/* Sur téléphone, le lien du titre devient un bouton large sous la liste. */}
                            <Link
                                href={articleTopicPath(locale, topic.id)}
                                className="md:hidden mt-4 block w-full py-3 rounded-xl text-center bg-gray-100 dark:bg-grayDark-800"
                            >
                                <span className="text-sm font-medium text-gray-800 dark:text-grayDark-200">
                                    {t("articles_see_topic", { count: topic.count })}
                                </span>
                            </Link>
                        </section>
                    </Reveal>
                ))}
        </>
    );
}

function Pagination({ listing }) {
    const t = useTranslations();
    const { onSwitch } = useContext(SwitchContext);
    const turn = () => onSwitch(0, "page");
    const locale = useLocale();
    const topics = useContext(TopicsContext);
    const { topic, subtopic, page, pages } = listing;
    if (pages <= 1) return null;

    const href = (number) => articleTopicPath(locale, topic, subtopic, number, topics);

    // La page courante, ses voisines, la première et la dernière.
    const shown = [];
    for (let number = 1; number <= pages; number++) {
        if (number === 1 || number === pages || Math.abs(number - page) <= 1) {
            shown.push(number);
        }
    }

    const round =
        "flex items-center justify-center w-11 h-11 md:w-9 md:h-9 rounded-full text-sm tabular-nums";
    const quiet = `${round} bg-gray-100 dark:bg-grayDark-800 hover:bg-gray-200 dark:hover:bg-grayDark-700`;
    const ink = "text-gray-700 dark:text-grayDark-300";

    return (
        <nav
            className="flex justify-center items-center gap-2 pt-8"
            aria-label={t("articles_pagination_label")}
        >
            {page > 1 && (
                <Link
                    href={href(page - 1)}
                    rel="prev"
                    aria-label={t("articles_page_previous")}
                    onClick={turn}
                    className={quiet}
                >
                    <SolarIcon name="alt-arrow-left-linear" className={`w-4 h-4 ${ink}`} />
                </Link>
            )}
            {shown.map((number, index) => (
                <span key={number} className="flex items-center gap-2">
                    {index > 0 && number - shown[index - 1] > 1 && (
                        <span className="text-gray-400" aria-hidden="true">
                            …
                        </span>
                    )}
                    {number === page ? (
                        <span aria-current="page" className={`${round} bg-primary text-white font-semibold`}>
                            {number}
                        </span>
                    ) : (
                        <Link href={href(number)} onClick={turn} className={quiet}>
                            <span className={ink}>{number}</span>
                        </Link>
                    )}
                </span>
            ))}
            {page < pages && (
                <Link
                    href={href(page + 1)}
                    rel="next"
                    aria-label={t("articles_page_next")}
                    onClick={turn}
                    className={quiet}
                >
                    <SolarIcon name="alt-arrow-right-linear" className={`w-4 h-4 ${ink}`} />
                </Link>
            )}
        </nav>
    );
}

/** Un sujet ouvert : ses sous-thèmes, puis ses articles du plus récent au plus ancien. */
function Listing({ listing, count, bookmark }) {
    const t = useTranslations();
    const locale = useLocale();
    const topics = useContext(TopicsContext);
    const topic = getArticleTopic(listing.topic, topics);
    const subtopics = topic.subtopics.filter((item) => listing.subtopicCounts[item.id] > 0);
    const { onSwitch } = useContext(SwitchContext);
    const keys = ["all", ...subtopics.map((item) => item.id)];
    const { containerRef, setItem, shown, pick, box, transition } = useSlidingIndicator(
        `subtopics-${listing.topic}`,
        listing.subtopic || "all",
    );

    return (
        <>
            {subtopics.length > 0 && (
                <SwitchingArea scope="topic">
                    <nav className={`${SCROLL_ROW} mb-6`} aria-label={t("articles_subtopics_label")}>
                        <div
                            ref={containerRef}
                            className="relative flex gap-5 w-max min-w-full pr-8 md:pr-0 border-b border-gray-200 dark:border-borderDark"
                        >
                            {/* Le trait orange glisse d'un sous-thème à l'autre. */}
                            {box && (
                                <span
                                    aria-hidden="true"
                                    style={{
                                        transform: `translate3d(${box.x}px, 0, 0)`,
                                        width: box.width,
                                        transition,
                                    }}
                                    className="absolute left-0 -bottom-px h-0.5 bg-primary pointer-events-none motion-reduce:!transition-none"
                                />
                            )}
                            {[null, ...subtopics].map((item) => {
                                const key = item?.id || "all";
                                const isShown = shown === key;
                                return (
                                    <Link
                                        key={key}
                                        ref={setItem(key)}
                                        href={articleTopicPath(locale, topic.id, item?.id, 1, topics)}
                                        aria-current={
                                            (listing.subtopic || "all") === key ? "page" : undefined
                                        }
                                        scroll={false}
                                        onClick={() => {
                                            pick(key);
                                            onSwitch(
                                                Math.sign(
                                                    keys.indexOf(key) -
                                                        keys.indexOf(listing.subtopic || "all"),
                                                ),
                                                "sub",
                                            );
                                        }}
                                        className={`-mb-px pt-1 pb-3 border-b-2 ${
                                            // Avant la première mesure, le trait est celui de l'onglet.
                                            isShown && !box ? "border-primary" : "border-transparent"
                                        }`}
                                    >
                                        <span
                                            // Même graisse pour tous : un libellé qui
                                            // s'épaissit pousserait ses voisins, et le
                                            // trait viserait une place qui bouge.
                                            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                                                isShown
                                                    ? "text-gray-900 dark:text-white"
                                                    : "text-gray-500 dark:text-grayDark-400"
                                            }`}
                                        >
                                            {item
                                                ? articleSubtopicLabel(t, topic.id, item.id, locale, topics)
                                                : t("articles_subtopic_all")}
                                            <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-grayDark-400 tabular-nums">
                                                {item ? listing.subtopicCounts[item.id] : count}
                                            </span>
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    </nav>
                </SwitchingArea>
            )}

            <SwitchingArea scope="page">
                {listing.articles.length === 0 ? (
                    <Alert>{t("articles_noArticles")}</Alert>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {listing.articles.map((article, index) => (
                                <Reveal key={article.id} index={index} className="h-full">
                                    <ArticleCard article={article} bookmark={bookmark} />
                                </Reveal>
                            ))}
                        </div>
                        <Pagination listing={listing} />
                    </>
                )}
            </SwitchingArea>
        </>
    );
}

/**
 * Les anciens liens filtraient par catégorie (/articles?category=developers) :
 * ils mènent à la page du sujet le plus proche.
 *
 * Isolé derrière un Suspense parce que useSearchParams fait basculer en rendu
 * navigateur tout ce qui l'entoure jusqu'à la première frontière Suspense. Lu
 * dans le composant de la page, il vidait le HTML servi : la liste précédente
 * partait sans un seul lien vers un article, seulement des cartes grises.
 */
function LegacyCategoryRedirect() {
    const searchParams = useSearchParams();
    const router = useRouter();
    const locale = useLocale();
    const topic = topicFromLegacyCategory(searchParams.get("category"));

    useEffect(() => {
        if (topic) router.replace(articleTopicPath(locale, topic));
    }, [topic, locale, router]);

    return null;
}

/**
 * Les pages d'articles : la vue d'ensemble (/articles) et la page d'un sujet.
 *
 * Tout ce qui s'affiche arrive en propriétés, déjà chargé par le serveur : le
 * HTML servi contient les titres et les liens. Le navigateur n'ajoute que les
 * signets d'un membre connecté.
 *
 * @param {object} props
 * @param {Record<string, number>} props.counts Nombre d'articles par sujet.
 * @param {{lead: object|null, mostRead: object[], topics: Array<{id: string, count: number, articles: object[]}>}} [props.overview]
 *        La vue d'ensemble. Absente sur la page d'un sujet.
 * @param {{topic: string, subtopic: string|null, page: number, pages: number, articles: object[], subtopicCounts: Record<string, number>}} [props.listing]
 *        Les articles d'un sujet. Absent sur la vue d'ensemble.
 * @param {Array} [props.topics] La liste des sujets, sous-thèmes créés depuis
 *        l'administration compris. Par défaut, ceux du code.
 */
export default function ArticlesByTopic({
    counts,
    overview = null,
    listing = null,
    topics = ARTICLE_TOPICS,
}) {
    const { data: session } = useSession();
    const t = useTranslations();
    const locale = useLocale();

    const [bookmarkedIds, setBookmarkedIds] = useState(null);
    useEffect(() => {
        if (!session) return;

        fetch("/api/article/bookmarked-ids", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => setBookmarkedIds(new Set(data?.articleIds || [])))
            .catch(() => setBookmarkedIds(new Set()));
    }, [session]);

    // Les signets attendent leur liste : un bouton monté avant elle garderait
    // l'état « non enregistré » même sur un article déjà mis de côté.
    const bookmark = session && bookmarkedIds ? { ids: bookmarkedIds } : null;

    // Une clé par vue : la vue d'ensemble, un sujet, un sous-thème, une page.
    const view = listing ? `${listing.topic}/${listing.subtopic || "all"}/${listing.page}` : "overview";

    return (
        <TopicsContext.Provider value={topics}>
        <SwitchProvider key={view} view={view}>
            {overview && (
                <Suspense fallback={null}>
                    <LegacyCategoryRedirect />
                </Suspense>
            )}

            <SwitchingArea scope="sub" slide={false}>
                <section className="mb-3">
                    <h1 className="md:text-4xl text-3xl dark:text-white font-bold font-sourceSans pt-5">
                        {!listing
                            ? t("articles_title")
                            : listing.subtopic
                              ? t("articles_subtopic_heading", {
                                    topic: t(`articles_topic_${listing.topic}`),
                                    subtopic: articleSubtopicLabel(
                                        t,
                                        listing.topic,
                                        listing.subtopic,
                                        locale,
                                        topics,
                                    ),
                                })
                              : t(`articles_topic_${listing.topic}`)}
                    </h1>
                    <p className="text-lg text-[#05060F]/70 dark:text-textSecondDark mt-4 mb-0 max-w-3xl">
                        {listing ? t(`articles_topic_${listing.topic}_intro`) : t("articles_headline")}
                    </p>
                </section>
            </SwitchingArea>

            <section className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mt-6 mb-8">
                <TopicTabs active={listing?.topic || "all"} counts={counts} />
                <Select onValueChange={(value) => window.open(value, "_blank")}>
                    <SelectTrigger className="w-[180px] shrink-0">
                        <SelectValue placeholder={t("articles_feeds_select")} />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-bgDark-840 dark:border-borderDark">
                        {labels.map((label) => (
                            <SelectItem key={label.id} value={`/feeds/articles-${label.id}.xml`}>
                                {label.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </section>

            <hr className="mb-8 dark:border-borderDark" />

            {listing ? (
                <Listing listing={listing} count={counts[listing.topic] || 0} bookmark={bookmark} />
            ) : (
                <SwitchingArea scope="topic">
                    <Overview overview={overview} bookmark={bookmark} />
                </SwitchingArea>
            )}
        </SwitchProvider>
        </TopicsContext.Provider>
    );
}
